import { useCallback, useMemo } from 'react';
import { type QueryParamConfig, type QueryParamConfigMap, useQueryParams } from 'use-query-params';
import type { FilterControl } from '../../../types/filterControl';
import { filterBuildersFor } from './filterBuilders';
import { excludedParam, type FilterLink, selectionParam, type Stored, toUrlKey } from './filterSelection';
import { type AnySpec, composeSpec, type FilterSpec, isFilterSpec, type UrlValues } from './filterSpecs';
import { brand, type Controls, type ControlSet, type FilterUnit } from './filterUnitType';

export type { FilterUnit } from './filterUnitType';

/**
 * Params every endpoint accepts. Bind a unit fed to several endpoints (list + counts/export) to this, never
 * to `A & B`: an intersection's keys are the union, so a param only one endpoint takes would type-check.
 * Three endpoints: nest it.
 */
export type SharedQuery<A extends object, B extends object> = Pick<A & B, keyof A & keyof B>;

/** Rejects keys the endpoint lacks, which a plain `extends` check lets through next to valid ones. */
type OnlyKeysOf<TInput, TQuery> = TInput & Record<Exclude<keyof TInput, keyof TQuery>, never>;

/** `[]` when every key of `TInput` is the endpoint's; otherwise a required argument that names the stray keys. */
type UnknownParams<TInput, TQuery> = [Exclude<keyof TInput, keyof TQuery>] extends [never]
  ? []
  : [unknownParams: `not params of this endpoint: ${Exclude<keyof TInput, keyof TQuery> & string}`];

/** Same check for an inferred input: `NoInfer` keeps the key check from steering what is inferred. */
type CheckedKeys<TInput, TQuery> = TInput & NoInfer<Record<Exclude<keyof TInput, keyof TQuery>, never>>;

type UnionToIntersection<U> = (U extends unknown ? (arg: U) => void : never) extends (arg: infer I) => void ? I : never;

const toControlSet = (controls: Controls): ControlSet =>
  Array.isArray(controls) ? { controls } : 'type' in controls ? { controls: [controls] } : controls;

// A composite may exclude under another name (`loadIndexGte` + `excludedLoadIndex`); any exclusion param counts.
const writesExclusions = (apiInput: object) => Object.keys(apiInput).some((key) => /^excluded?[A-Z]/.test(key));

const canExclude = (control: FilterControl) => 'canExclude' in control && control.canExclude;

// The serializer drops empty arrays anyway; normalizing keeps query keys stable across [] and undefined.
const withoutEmptyArrays = <TInput extends object>(apiInput: TInput): TInput =>
  Object.fromEntries(
    Object.entries(apiInput).map(([key, value]) => [
      key,
      Array.isArray(value) && value.length === 0 ? undefined : value
    ])
  ) as TInput;

/**
 * A `compose` unit. A control offering Exclude in a unit that writes no exclusion param would be a toggle that
 * filters nothing, so it throws rather than shipping one.
 */
const buildUnit = <TInput extends object>(controls: Controls, apiInput: TInput): FilterUnit<TInput> => {
  const set = toControlSet(controls);
  const silentExclude = set.controls?.find((control) => canExclude(control) && !writesExclusions(apiInput));
  if (silentExclude) {
    throw new Error(`Filter "${silentExclude.id}" offers Exclude but writes no exclusion param`);
  }
  return brand({ ...set, controls: set.controls ?? [], apiInput: withoutEmptyArrays(apiInput) });
};

/**
 * Unit factory bound to one endpoint's query params. Filters are its builders (`multiSelect`, `singleSelect`,
 * `boolean`, `text`, `date`, `dateRange`, `numberRange`, `timeRange`, `toolbarDateRange`): each names an nsdk param
 * and owns control, URL state and param; resolve them with `useFilterUnits`. Builders on stand-in params mapped to
 * this endpoint: `map`. Anything else on the same URL state: `compose`.
 * Several endpoints fed the same input: bind to `SharedQuery` of their queries.
 */
export const filterUnitFor = <TQuery extends object>() =>
  Object.assign(
    /**
     * Controls over state that is not the URL (a picker's local selection) with the params they write. URL-backed
     * filters are builders or `compose`.
     */
    <TInput extends TQuery>(controls: Controls, apiInput: OnlyKeysOf<TInput, TQuery>): FilterUnit<TInput> =>
      buildUnit(controls, apiInput),
    {
      /** Params the page fixes (route id, scope, constant). No control; still one writer per param. */
      locked: <TInput extends TQuery>(apiInput: OnlyKeysOf<TInput, TQuery>): FilterUnit<TInput> =>
        brand({ controls: [], apiInput }),
      /**
       * A composite filter on the builders' URL state: declare its URL params (`filterUrlState`), build controls +
       * params from their values; `setValues` merges into the latest URL. `name` keys the decoded configs (with the specs'
       * own signatures, when given specs): decode loosely (`parseNumber`) and validate in `resolve`, or put what a
       * decoder depends on into `name`.
       */
      compose: <TInput extends TQuery, TUrl extends UrlParamsInput = UrlParamsRecord>(
        name: string,
        /**
         * URL params by URL key, or the specs whose URL state the composite reads (resolve them in `resolve`):
         * then links into the list can set them, as they can a builder's.
         */
        urlParams: TUrl,
        resolve: (
          values: UrlValues,
          setValues: (changes: (latest: UrlValues) => UrlValues) => void
        ) => { controls: Controls; params: CheckedKeys<TInput, TQuery> },
        /** Ids of the controls it renders, so `filters.controlOf(spec)` finds them. */
        controlIds: readonly string[] = []
      ): FilterSpec<TInput, FilterControl, ComposeLink<TUrl>> =>
        composeSpec<TInput, ComposeLink<TUrl>>(
          // Specs decode by their options: their signatures key the configs, so changed options re-decode.
          isSpecList(urlParams) ? `${name}:${urlParams.map((spec) => spec.signature).join('+')}` : name,
          isSpecList(urlParams) ? mergeUrlParams(urlParams) : urlParams,
          (values, setValues) => {
            const { controls, params } = resolve(values, setValues);
            return buildUnit(controls, params);
          },
          controlIds
        ),
      /**
       * Builders on stand-in params (a `filterUnitFor<{ serviceIds?: number[]; ... }>()` the URL alone holds), mapped
       * onto this endpoint's params: two pickers narrowing one param, a range the endpoint names `Min`/`Max`, minutes
       * sent as seconds, loaded names guarded into a closed union. Controls, URL state and Reset all stay the
       * builders'; only the params are rewritten. Prefer it to `compose` whenever builders draw the controls.
       */
      map: <TSpecs extends AnySpec[], TInput extends TQuery>(
        specs: [...TSpecs],
        toParams: (params: UnionToIntersection<ParamsOf<TSpecs[number]>>) => TInput,
        // A mapped param the endpoint lacks asks for an argument no caller passes, naming the param.
        ..._onlyEndpointParams: UnknownParams<TInput, TQuery>
      ): FilterSpec<TInput, FilterControl, UnionToIntersection<LinkOf<TSpecs[number]>>> =>
        composeSpec<TInput, UnionToIntersection<LinkOf<TSpecs[number]>>>(
          `map:${specs.map((spec) => spec.signature).join('+')}`,
          mergeUrlParams(specs),
          (values, setValues) => {
            const { controls, quickFilter, dateRange, apiInput } = combineFilterUnits(
              specs.map((spec) => spec.resolve(values, setValues))
            );
            // Mapped params carry the exclusions on: a builder's Exclude with none in them throws (`buildUnit`).
            return buildUnit(
              { controls, quickFilter, dateRange },
              toParams(apiInput as UnionToIntersection<ParamsOf<TSpecs[number]>>)
            );
          },
          specs.flatMap((spec) => spec.controlIds)
        ),
      /**
       * Builders whose values the page reads first (`useFilterSpecValue`) and resolves through a lookup into these
       * params: roles and data quality to the user groups a query returns. Controls, URL state and Reset all stay the
       * builders'. Their exclusions reach the lookup, not a param here, so nothing checks them: read every builder's
       * value, exclusions included, into the lookup.
       */
      lookup: <TSpecs extends AnySpec[], TInput extends TQuery>(
        specs: [...TSpecs],
        params: OnlyKeysOf<TInput, TQuery>
      ): FilterSpec<TInput, FilterControl, UnionToIntersection<LinkOf<TSpecs[number]>>> =>
        composeSpec<TInput, UnionToIntersection<LinkOf<TSpecs[number]>>>(
          `lookup:${specs.map((spec) => spec.signature).join('+')}`,
          mergeUrlParams(specs),
          (values, setValues) => {
            const { controls, quickFilter, dateRange } = combineFilterUnits(
              specs.map((spec) => spec.resolve(values, setValues))
            );
            return brand({ controls, quickFilter, dateRange, apiInput: withoutEmptyArrays(params as TInput) });
          },
          specs.flatMap((spec) => spec.controlIds)
        )
    },
    filterBuildersFor<TQuery>()
  );

/** Display order is unit order. Each param has one writer; a second would silently drop a filter. */
export const combineFilterUnits = <TUnits extends FilterUnit<object>[]>(
  units: [...TUnits]
): FilterUnit<UnionToIntersection<TUnits[number]['apiInput']>> => {
  const apiInput: Record<string, unknown> = {};
  for (const unit of units) {
    for (const [key, value] of Object.entries(unit.apiInput)) {
      if (key in apiInput) {
        throw new Error(`Two filters write "${key}"`);
      }
      apiInput[key] = value;
    }
  }
  const quickFilters = units.flatMap((unit) => (unit.quickFilter ? [unit.quickFilter] : []));
  const dateRanges = units.flatMap((unit) => (unit.dateRange ? [unit.dateRange] : []));
  if (quickFilters.length > 1 || dateRanges.length > 1) {
    throw new Error('A table has at most one quick filter and one date range');
  }
  return brand({
    controls: units.flatMap((unit) => unit.controls),
    quickFilter: quickFilters[0],
    dateRange: dateRanges[0],
    apiInput: apiInput as UnionToIntersection<TUnits[number]['apiInput']>
  });
};

type ParamsOf<TItem> =
  TItem extends FilterUnit<infer TParams>
    ? TParams
    : TItem extends FilterSpec<infer TParams, FilterControl, object>
      ? TParams
      : never;

/** What a link may set through this item: a spec's URL params; nothing through a unit (locked, local state). */
type LinkOf<TItem> = TItem extends { readonly link?: infer TLink } ? (unknown extends TLink ? object : TLink) : object;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each composite owns its value types
type UrlParamsRecord = Record<string, QueryParamConfig<any, any>>;
type UrlParamsInput = UrlParamsRecord | readonly AnySpec[];

/** Specs given: links set what they read. URL params by key, or the input type passed explicitly: nothing. */
type ComposeLink<TUrl> = [TUrl] extends [readonly AnySpec[]] ? UnionToIntersection<LinkOf<TUrl[number]>> : object;

const isSpecList = (urlParams: UrlParamsInput): urlParams is readonly AnySpec[] => Array.isArray(urlParams);

const mergeUrlParams = (specs: readonly AnySpec[]): QueryParamConfigMap =>
  Object.assign({}, ...specs.map((spec) => spec.urlParams));

/** `cond && spec` leaves a filter out (hidden filter = no control, no params). */
type FilterItem = FilterUnit<object> | AnySpec | false | null | undefined;

/**
 * A list's filters: builder specs resolved against the URL (one `useQueryParams` for all of them) and combined
 * with composite units, in display order. Its `apiInput` is the only source of filter params: spread it into the
 * list query and every request meaning "the filtered set" (counts, export, select-all, map).
 */
export const useFilterUnits = <TItems extends FilterItem[]>(
  items: [...TItems]
): ListFilters<UnionToIntersection<ParamsOf<TItems[number]>>, UnionToIntersection<LinkOf<TItems[number]>>> => {
  const present = items.filter((item): item is FilterUnit<object> | AnySpec => !!item);
  const [values, setValues] = useSpecUrlValues(present.filter(isFilterSpec));
  const filters = combineFilterUnits(
    present.map((item) =>
      isFilterSpec(item) ? item.resolve(values, (changes) => setValues(changes, 'replaceIn')) : item
    )
  ) as FilterUnit<UnionToIntersection<ParamsOf<TItems[number]>>>;
  return {
    ...filters,
    controlOf: <TControl extends FilterControl>(spec: FilterSpec<object, TControl, object>) =>
      filters.controls.find((control) => spec.controlIds.includes(control.id)) as TControl | undefined
  };
};

/**
 * A list's resolved filters, plus typed access to one spec's control for UI outside the sidebar (a chart that
 * filters on click, a header date picker): the same value and atomic `onChange` the sidebar uses, never a URL key
 * written by hand. Pass the spec object given to `useFilterUnits`.
 */
export type ListFilters<TInput, TLink = object> = FilterUnit<TInput> & {
  controlOf: <TControl extends FilterControl>(spec: FilterSpec<object, TControl, object>) => TControl | undefined;
  /** Type-only: what a link into this list may set (`FilterLinkOf`). */
  readonly link?: TLink;
};

type Linked = { readonly link?: unknown };

/**
 * The link type of a list, from its filters hook (`FilterLinkOf<typeof usePartnerBookingFilters>`) or, where the
 * list assembles its filters at runtime, from the spec a link targets: the keys they read from the URL, for
 * `filterSearch`. Export it next to the hook; link producers import that name.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any hook arguments
export type FilterLinkOf<TSource extends ((...args: any[]) => Linked) | Linked> = FilterLink<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- any hook arguments
  NonNullable<(TSource extends (...args: any[]) => infer TFilters ? TFilters : TSource)['link']>
>;

const useSpecUrlValues = (specs: AnySpec[]) => {
  const signature = specs.map((spec) => spec.signature).join('|');
  const paramConfigs = useMemo(
    (): QueryParamConfigMap => Object.assign({}, ...specs.map((spec) => spec.urlParams)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- specs are rebuilt every render; their signature names everything the configs decode by
    [signature]
  );
  return useQueryParams(paramConfigs, { updateType: 'replaceIn' });
};

/**
 * The params one builder writes, read before the list's `useFilterUnits` (options that depend on another
 * filter: models of the picked makes). Same URL state, so it never drifts from the control.
 */
export const useFilterSpecValue = <TParams>(spec: FilterSpec<TParams, FilterControl, object>): TParams => {
  const [values] = useSpecUrlValues([spec as AnySpec]);
  return spec.resolve(values, () => undefined).apiInput;
};

const excludedKeyOf = (key: string) => `excluded${key.charAt(0).toUpperCase()}${key.slice(1)}`;

/**
 * What a multi-select builder on `key` holds in the URL (include and exclude), read by param name before its spec
 * exists: a source that must keep its own picked values loadable (an archived facility while it is picked).
 */
export const useFilterSelectionValues = <T extends string | number>(
  key: string,
  parse: (value: string) => T | undefined
): { include: T[]; exclude: T[] } => {
  const includeUrlKey = toUrlKey(key);
  const excludeUrlKey = toUrlKey(excludedKeyOf(key));
  const configs = useMemo(
    (): QueryParamConfigMap => ({ [includeUrlKey]: selectionParam(parse), [excludeUrlKey]: excludedParam(parse) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the parser is fixed per param; keyed by its URL keys
    [includeUrlKey, excludeUrlKey]
  );
  const [values] = useQueryParams(configs);
  return {
    include: (values[includeUrlKey] as Stored<T[]>) ?? [],
    exclude: (values[excludeUrlKey] as T[] | undefined) ?? []
  };
};

/**
 * Clears builder params by name (include and exclusion), back to their defaults, for a page whose rule spans
 * filters it does not render (a date change that invalidates the worker picked in each tab). URL keys stay the
 * builders'.
 */
export const useClearFilterParams = <TQuery extends object>() => {
  const [, setValues] = useQueryParams({});
  return useCallback(
    (keys: Extract<keyof TQuery, string>[]) =>
      setValues(
        Object.fromEntries(
          keys.flatMap((key) => [toUrlKey(key), toUrlKey(excludedKeyOf(key))].map((urlKey) => [urlKey, undefined]))
        ),
        'replaceIn'
      ),
    [setValues]
  );
};

/**
 * Decorates each control (counts, options) without touching what it writes. Cannot add an exclude the unit
 * dropped: controls whose params live elsewhere belong in a unit bound to that endpoint.
 */
export const mapFilterControls = <TUnit extends FilterUnit<unknown>>(
  unit: TUnit,
  map: (control: FilterControl) => FilterControl
): TUnit => ({
  ...unit,
  controls: unit.controls.map((control) => {
    const mapped = map(control);
    return canExclude(control) || !('canExclude' in mapped) ? mapped : { ...mapped, canExclude: false };
  })
});
